import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TableContainer,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Add,
  ArrowBack,
  Check,
  CloudUpload,
  Close,
  Dashboard,
  Delete,
  Description,
  Edit,
} from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import * as XLSX from 'xlsx';

ModuleRegistry.registerModules([AllCommunityModule]);
const LEGACY_STORAGE_KEY = 'financeiro_processos_dashboard';
const PROCESSOS_UPLOAD_ENTIDADE = 'financeiro_processos';
const FORCED_HIDDEN_HEADERS = new Set(['coluna 1', 'coluna 2', 'coluna 17']);

function parseDateValue(value) {
  const raw = cleanText(value);
  if (!raw) return null;

  const brMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (brMatch) {
    const day = Number(brMatch[1]);
    const month = Number(brMatch[2]) - 1;
    const year = Number(brMatch[3].length === 2 ? `20${brMatch[3]}` : brMatch[3]);
    const date = new Date(year, month, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const iso = new Date(raw);
  return Number.isNaN(iso.getTime()) ? null : iso;
}

function formatDateBrDisplay(value) {
  const parsed = parseDateValue(value);
  if (!parsed) return cleanText(value);

  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = String(parsed.getFullYear());
  return `${day}/${month}/${year}`;
}

function isLikelyDateHeader(header) {
  const normalized = cleanText(header)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  return [
    'data',
    'vencimento',
    'prazo',
    'entrada',
    'realizacao',
    'protocolo',
    'aprovacao',
    'limite',
  ].some((term) => normalized.includes(term));
}

function pickPreferredUpload(uploads) {
  if (!Array.isArray(uploads) || uploads.length === 0) return null;

  const scoreUpload = (upload) => {
    const name = cleanText(upload?.nome).toLowerCase();
    let score = 0;
    if (name.includes('plan-prefeitura')) score += 100;
    if (name.includes('processo')) score += 50;
    if (name.includes('thors')) score += 40;
    if (name.includes('modulo-acompanhamento')) score += 40;
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) score += 10;
    return score;
  };

  return [...uploads].sort((a, b) => {
    const scoreDiff = scoreUpload(b) - scoreUpload(a);
    if (scoreDiff !== 0) return scoreDiff;
    return Number(b?.id || 0) - Number(a?.id || 0);
  })[0];
}

function cleanText(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\s+/g, ' ').trim();
}

function normalizeFieldName(header, index) {
  const cleaned = cleanText(header)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return cleaned ? `col_${cleaned}_${index}` : `coluna_${index}`;
}

function shouldForceHideHeader(header) {
  return FORCED_HIDDEN_HEADERS.has(cleanText(header).toLowerCase());
}

function detectHeaderRow(rows) {
  let bestIndex = 0;
  let bestScore = -1;

  rows.slice(0, 60).forEach((row, rowIndex) => {
    const values = (row || []).map(cleanText).filter(Boolean);
    if (!values.length) return;

    const joined = values.join(' ').toLowerCase();
    const knownFieldsMatch =
      joined.includes('processo') ||
      joined.includes('interessado') ||
      joined.includes('unidade') ||
      joined.includes('endereco') ||
      joined.includes('e-mail') ||
      joined.includes('email');

    const score = values.length + (knownFieldsMatch ? 8 : 0);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = rowIndex;
    }
  });

  return bestIndex;
}

function compactDataset(headers, fields, rows) {
  const keepIndexes = headers
    .map((header, index) => {
      const field = fields[index];
      const label = cleanText(header).toLowerCase();
      if (shouldForceHideHeader(label)) return null;
      const isGenericColumn = /^coluna\s+\d+$/i.test(label);
      const hasValue = rows.some((row) => cleanText(row[field]));

      if (hasValue) return index;
      if (isGenericColumn) return null;
      return null;
    })
    .filter((index) => index !== null);

  const fallbackIndexes = keepIndexes.length ? keepIndexes : [0];
  const nextHeaders = fallbackIndexes.map((index) => headers[index]);
  const nextFields = fallbackIndexes.map((index) => fields[index]);

  const nextRows = rows
    .map((row) => {
      const nextRow = { id: row.id };
      nextFields.forEach((field) => {
        nextRow[field] = row[field] || '';
      });
      if (row.grupo) {
        nextRow.grupo = row.grupo;
      }
      return nextRow;
    })
    .filter((row) => nextFields.some((field) => cleanText(row[field])));

  return {
    headers: nextHeaders,
    fields: nextFields,
    rows: nextRows,
  };
}

function parseWorkbook(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = new Uint8Array(reader.result);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false });

        if (!rawRows.length) {
          reject(new Error('Planilha vazia.'));
          return;
        }

        const headerRowIndex = detectHeaderRow(rawRows);
        const rawHeaders = (rawRows[headerRowIndex] || []).map(
          (h, idx) => cleanText(h) || `Coluna ${idx + 1}`
        );
        const fields = rawHeaders.map((h, idx) => normalizeFieldName(h, idx));

        let currentGrupo = '';
        const parsedRows = [];

        rawRows.slice(headerRowIndex + 1).forEach((rawRow) => {
          const normalized = fields.map((_, idx) => cleanText(rawRow?.[idx]));
          const notEmptyValues = normalized.filter(Boolean);

          if (!notEmptyValues.length) return;

          const onlyFirstHasText = normalized[0] && normalized.slice(1).every((value) => !value);
          const isSectionRow =
            onlyFirstHasText && /(em\s+andamento|grupo|etapa|status)/i.test(normalized[0]);

          if (isSectionRow) {
            currentGrupo = normalized[0];
            return;
          }

          const rowData = { id: `${Date.now()}_${parsedRows.length}` };
          fields.forEach((field, index) => {
            rowData[field] = normalized[index] || '';
          });

          if (currentGrupo) {
            rowData.grupo = currentGrupo;
          }

          parsedRows.push(rowData);
        });

        const compactRows = parsedRows.filter((row) => {
          const hasContent = fields.some((field) => cleanText(row[field]));
          return hasContent;
        });

        const compactData = compactDataset(rawHeaders, fields, compactRows);

        resolve({
          headers: compactData.headers,
          fields: compactData.fields,
          rows: compactData.rows,
          fileName: file.name,
        });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Nao foi possivel ler o arquivo.'));
    reader.readAsArrayBuffer(file);
  });
}

function datasetToWorkbookFile(dataset, fileName = 'processos_recuperado.xlsx') {
  const headers = Array.isArray(dataset?.headers) ? dataset.headers : [];
  const fields = Array.isArray(dataset?.fields) ? dataset.fields : [];
  const rows = Array.isArray(dataset?.rows) ? dataset.rows : [];
  const includeGrupo = rows.some((row) => cleanText(row.grupo));
  const sheetHeaders = includeGrupo ? [...headers, 'Grupo'] : headers;
  const aoaRows = rows.map((row) => {
    const values = fields.map((field) => row[field] || '');
    return includeGrupo ? [...values, row.grupo || ''] : values;
  });
  const worksheet = XLSX.utils.aoa_to_sheet([sheetHeaders, ...aoaRows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Processos');
  const arrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new File([arrayBuffer], fileName, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export default function ProcessosFinanceiros() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dataset, setDataset] = useState({ headers: [], fields: [], rows: [], fileName: '' });
  const [error, setError] = useState('');
  const [loadingShared, setLoadingShared] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [groupFilter, setGroupFilter] = useState('TODOS');
  const [unitFilter, setUnitFilter] = useState('TODAS');
  const [statusFilter, setStatusFilter] = useState('TODOS');
  const [orgaoFilter, setOrgaoFilter] = useState('TODOS');
  const [quickViewMode, setQuickViewMode] = useState('cadastro');
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingRowBackup, setEditingRowBackup] = useState(null);

  const API = '/api';

  const uploadSharedDatasetFile = useCallback(
    async (file) => {
      if (!token || !file) return false;

      const formData = new FormData();
      formData.append('file', file);

      const uploadRes = await fetch(
        `${API}/uploads/${encodeURIComponent(PROCESSOS_UPLOAD_ENTIDADE)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!uploadRes.ok) {
        let msg = `Falha ao salvar planilha compartilhada (HTTP ${uploadRes.status}).`;
        try {
          const uploadErr = await uploadRes.json();
          if (uploadErr?.detail) msg = uploadErr.detail;
        } catch {}
        throw new Error(msg);
      }

      return true;
    },
    [token]
  );

  const loadLegacyDataset = useCallback(() => {
    try {
      const cached = window.localStorage.getItem(LEGACY_STORAGE_KEY);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      if (!parsed?.rows?.length || !parsed?.headers?.length || !parsed?.fields?.length) {
        return null;
      }

      const compactData = compactDataset(parsed.headers, parsed.fields, parsed.rows);
      return {
        headers: compactData.headers,
        fields: compactData.fields,
        rows: compactData.rows,
        fileName: parsed.fileName || 'processos_recuperado.xlsx',
      };
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const loadSharedDataset = async () => {
      if (!token) return;

      setLoadingShared(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const listRes = await fetch(
          `${API}/uploads?entidade=${encodeURIComponent(PROCESSOS_UPLOAD_ENTIDADE)}`,
          { headers }
        );
        if (!listRes.ok) return;

        const uploads = await listRes.json();
        if (!Array.isArray(uploads) || uploads.length === 0) {
          const legacyDataset = loadLegacyDataset();
          if (legacyDataset) {
            setDataset(legacyDataset);
            setError(
              'Planilha anterior recuperada e republicada para os usuários com acesso ao financeiro.'
            );
            await uploadSharedDatasetFile(
              datasetToWorkbookFile(
                legacyDataset,
                legacyDataset.fileName || 'processos_recuperado.xlsx'
              )
            );
          }
          return;
        }

        const preferredUpload = pickPreferredUpload(uploads);
        if (!preferredUpload?.id) return;

        const fileRes = await fetch(`${API}/uploads/${preferredUpload.id}/download`, { headers });
        if (!fileRes.ok) return;

        const blob = await fileRes.blob();
        const fileName = preferredUpload.nome || 'processos.xlsx';
        const file = new File([blob], fileName, {
          type: blob.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const parsed = await parseWorkbook(file);
        setDataset(parsed);
        window.localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(parsed));
      } catch (loadError) {
        console.error('Erro ao carregar planilha compartilhada de Processos:', loadError);
        const legacyDataset = loadLegacyDataset();
        if (legacyDataset) {
          setDataset(legacyDataset);
          setError(
            'Planilha anterior recuperada do navegador. Tente importar novamente caso ela não apareça em outros logins.'
          );
        }
      } finally {
        setLoadingShared(false);
      }
    };

    loadSharedDataset();
  }, [loadLegacyDataset, token, uploadSharedDatasetFile]);

  const columnMeta = useMemo(() => {
    return dataset.headers.map((header, index) => ({
      header,
      field: dataset.fields[index],
    }));
  }, [dataset.fields, dataset.headers]);

  const fieldByName = useMemo(() => {
    const lowerMap = columnMeta.map((column) => ({
      ...column,
      normalizedHeader: column.header
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''),
    }));

    const findField = (terms) => {
      const found = lowerMap.find((column) =>
        terms.some((term) => column.normalizedHeader.includes(term))
      );
      return found?.field || '';
    };

    return {
      processo: findField(['processo', 'protocolo']),
      interessado: findField(['interessado', 'requerente', 'nome']),
      cliente: findField(['cliente']),
      orgao: findField(['orgao', 'órgão', 'secretaria', 'suborgao', 'subórgão']),
      tipo: findField(['tipo']),
      status: findField(['status', 'situacao', 'situação']),
      prazo: findField(['prazo', 'vencimento', 'data_limite', 'data limite']),
      unidade: findField(['unidade', 'setor', 'secretaria']),
      email: findField(['e-mail', 'email']),
    };
  }, [columnMeta]);

  const hasGrupoColumn = useMemo(
    () => dataset.rows.some((row) => cleanText(row.grupo)),
    [dataset.rows]
  );

  const availableGroups = useMemo(() => {
    if (!hasGrupoColumn) return [];
    return Array.from(
      new Set(dataset.rows.map((row) => cleanText(row.grupo)).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
  }, [dataset.rows, hasGrupoColumn]);

  const availableUnits = useMemo(() => {
    if (!fieldByName.unidade) return [];
    return Array.from(
      new Set(dataset.rows.map((row) => cleanText(row[fieldByName.unidade])).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
  }, [dataset.rows, fieldByName.unidade]);

  const availableStatuses = useMemo(() => {
    if (!fieldByName.status) return [];
    return Array.from(
      new Set(dataset.rows.map((row) => cleanText(row[fieldByName.status])).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
  }, [dataset.rows, fieldByName.status]);

  const availableOrgaos = useMemo(() => {
    if (!fieldByName.orgao) return [];
    return Array.from(
      new Set(dataset.rows.map((row) => cleanText(row[fieldByName.orgao])).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
  }, [dataset.rows, fieldByName.orgao]);

  const filteredRows = useMemo(() => {
    const search = cleanText(searchText).toLowerCase();

    return dataset.rows.filter((row) => {
      if (groupFilter !== 'TODOS' && cleanText(row.grupo) !== groupFilter) {
        return false;
      }

      if (unitFilter !== 'TODAS' && fieldByName.unidade) {
        if (cleanText(row[fieldByName.unidade]) !== unitFilter) {
          return false;
        }
      }

      if (statusFilter !== 'TODOS' && fieldByName.status) {
        if (cleanText(row[fieldByName.status]) !== statusFilter) {
          return false;
        }
      }

      if (orgaoFilter !== 'TODOS' && fieldByName.orgao) {
        if (cleanText(row[fieldByName.orgao]) !== orgaoFilter) {
          return false;
        }
      }

      if (quickViewMode === 'comunique') {
        const statusValue = fieldByName.status
          ? cleanText(row[fieldByName.status]).toLowerCase()
          : '';
        const textoLivre = [
          ...columnMeta.map((column) => cleanText(row[column.field])),
          cleanText(row.grupo),
        ]
          .join(' ')
          .toLowerCase();
        const isComunique =
          statusValue.includes('comunique') ||
          statusValue.includes('penden') ||
          textoLivre.includes('comunique-se') ||
          textoLivre.includes('comunique') ||
          textoLivre.includes('pendencia');
        if (!isComunique) return false;
      }

      if (quickViewMode === 'alertas') {
        if (!fieldByName.prazo) return false;
        const prazoDate = parseDateValue(row[fieldByName.prazo]);
        if (!prazoDate) return false;

        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const in15Days = new Date(now);
        in15Days.setDate(in15Days.getDate() + 15);

        const isInAlertWindow = prazoDate < now || (prazoDate >= now && prazoDate <= in15Days);
        if (!isInAlertWindow) return false;
      }

      if (!search) {
        return true;
      }

      const combined = [
        ...columnMeta.map((column) => cleanText(row[column.field])),
        cleanText(row.grupo),
      ]
        .join(' ')
        .toLowerCase();

      return combined.includes(search);
    });
  }, [
    columnMeta,
    dataset.rows,
    fieldByName.orgao,
    fieldByName.prazo,
    fieldByName.status,
    fieldByName.unidade,
    groupFilter,
    orgaoFilter,
    quickViewMode,
    searchText,
    statusFilter,
    unitFilter,
  ]);

  const sortedRows = useMemo(() => {
    if (!fieldByName.prazo) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const dateA = parseDateValue(a[fieldByName.prazo]);
      const dateB = parseDateValue(b[fieldByName.prazo]);

      if (dateA && dateB) return dateA.getTime() - dateB.getTime();
      if (dateA && !dateB) return -1;
      if (!dateA && dateB) return 1;
      return 0;
    });
  }, [fieldByName.prazo, filteredRows]);

  const dashboard = useMemo(() => {
    const totalRegistros = filteredRows.length;
    const grupos = new Set(filteredRows.map((row) => cleanText(row.grupo)).filter(Boolean));

    const totalProcessosUnicos = fieldByName.processo
      ? new Set(filteredRows.map((row) => cleanText(row[fieldByName.processo])).filter(Boolean))
          .size
      : 0;

    const totalEmails = fieldByName.email
      ? filteredRows.filter((row) => cleanText(row[fieldByName.email]).includes('@')).length
      : 0;

    const totalComPrazo = fieldByName.prazo
      ? filteredRows.filter((row) => parseDateValue(row[fieldByName.prazo])).length
      : 0;

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const totalAtrasados = fieldByName.prazo
      ? filteredRows.filter((row) => {
          const date = parseDateValue(row[fieldByName.prazo]);
          return date ? date < now : false;
        }).length
      : 0;

    return {
      totalRegistros,
      totalProcessosUnicos,
      totalEmails,
      totalGrupos: grupos.size,
      totalComPrazo,
      totalAtrasados,
    };
  }, [fieldByName.email, fieldByName.prazo, fieldByName.processo, filteredRows]);

  const painelGestao = useMemo(() => {
    const total = Math.max(filteredRows.length, 1);

    const statusCountsMap = new Map();
    const orgaoCountsMap = new Map();

    filteredRows.forEach((row) => {
      const statusRaw = fieldByName.status ? cleanText(row[fieldByName.status]) : '';
      const statusKey = statusRaw || 'Sem status';
      statusCountsMap.set(statusKey, (statusCountsMap.get(statusKey) || 0) + 1);

      const orgaoRaw = fieldByName.orgao ? cleanText(row[fieldByName.orgao]) : '';
      const orgaoKey = orgaoRaw || 'Nao informado';
      orgaoCountsMap.set(orgaoKey, (orgaoCountsMap.get(orgaoKey) || 0) + 1);
    });

    const statusCounts = Array.from(statusCountsMap.entries())
      .map(([label, value]) => ({
        label,
        value,
        percent: Math.round((value / total) * 100),
      }))
      .sort((a, b) => b.value - a.value);

    const orgaoCounts = Array.from(orgaoCountsMap.entries())
      .map(([label, value]) => ({
        label,
        value,
        percent: Math.round((value / total) * 100),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    const aprovados = filteredRows.filter((row) => {
      const statusValue = fieldByName.status
        ? cleanText(row[fieldByName.status]).toLowerCase()
        : '';
      return statusValue.includes('aprov') || statusValue.includes('defer');
    }).length;

    const comuniquePendentes = filteredRows.filter((row) => {
      const statusValue = fieldByName.status
        ? cleanText(row[fieldByName.status]).toLowerCase()
        : '';
      return statusValue.includes('comunique') || statusValue.includes('penden');
    }).length;

    const ativos = filteredRows.filter((row) => {
      const statusValue = fieldByName.status
        ? cleanText(row[fieldByName.status]).toLowerCase()
        : '';
      return !statusValue.includes('finaliz') && !statusValue.includes('indefer');
    }).length;

    return {
      statusCounts,
      orgaoCounts,
      aprovados,
      comuniquePendentes,
      ativos,
    };
  }, [fieldByName.orgao, fieldByName.status, filteredRows]);

  const alertasPrazo = useMemo(() => {
    if (!fieldByName.prazo) return { proximos: [], atrasados: [] };

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const in15Days = new Date(now);
    in15Days.setDate(in15Days.getDate() + 15);

    const items = sortedRows
      .map((row) => {
        const prazoDate = parseDateValue(row[fieldByName.prazo]);
        if (!prazoDate) return null;
        return {
          id: row.id,
          processo: fieldByName.processo ? cleanText(row[fieldByName.processo]) : cleanText(row.id),
          status: fieldByName.status ? cleanText(row[fieldByName.status]) : '',
          prazo: prazoDate,
        };
      })
      .filter(Boolean);

    const proximos = items
      .filter((item) => item.prazo >= now && item.prazo <= in15Days)
      .slice(0, 6);

    const atrasados = items.filter((item) => item.prazo < now).slice(0, 6);

    return { proximos, atrasados };
  }, [fieldByName.prazo, fieldByName.processo, fieldByName.status, sortedRows]);

  const timelineProcessos = useMemo(() => {
    const dateField =
      columnMeta.find((col) => {
        const normalized = cleanText(col.header).toLowerCase();
        return normalized.includes('data') || normalized.includes('protocolo');
      })?.field || fieldByName.prazo;

    if (!dateField) return [];

    return filteredRows
      .map((row) => {
        const eventDate = parseDateValue(row[dateField]);
        if (!eventDate) return null;

        return {
          id: row.id,
          date: eventDate,
          processo: fieldByName.processo ? cleanText(row[fieldByName.processo]) : cleanText(row.id),
          status: fieldByName.status ? cleanText(row[fieldByName.status]) : '',
          orgao: fieldByName.orgao ? cleanText(row[fieldByName.orgao]) : '',
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 8);
  }, [
    columnMeta,
    fieldByName.orgao,
    fieldByName.prazo,
    fieldByName.processo,
    fieldByName.status,
    filteredRows,
  ]);

  const handleCellChange = (rowId, field, value) => {
    setDataset((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
    }));
  };

  const handleAddRow = () => {
    if (!dataset.fields.length) return;
    const newId = `row_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const newRow = { id: newId };
    dataset.fields.forEach((field) => {
      newRow[field] = '';
    });
    if (hasGrupoColumn) {
      newRow.grupo = '';
    }

    setDataset((prev) => ({
      ...prev,
      rows: [newRow, ...prev.rows],
    }));

    setEditingRowId(newId);
    setEditingRowBackup(null);
  };

  const handleDeleteRow = useCallback(
    (rowId) => {
      setDataset((prev) => ({
        ...prev,
        rows: prev.rows.filter((row) => row.id !== rowId),
      }));

      if (editingRowId === rowId) {
        setEditingRowId(null);
        setEditingRowBackup(null);
      }
    },
    [editingRowId]
  );

  const handleStartRowEdit = useCallback((rowData) => {
    if (!rowData?.id) return;
    setEditingRowId(rowData.id);
    setEditingRowBackup({ ...rowData });
  }, []);

  const handleSaveRowEdit = useCallback(() => {
    setEditingRowId(null);
    setEditingRowBackup(null);
  }, []);

  const handleCancelRowEdit = useCallback(() => {
    if (editingRowId && editingRowBackup) {
      setDataset((prev) => ({
        ...prev,
        rows: prev.rows.map((row) => (row.id === editingRowId ? { ...editingRowBackup } : row)),
      }));
    }
    setEditingRowId(null);
    setEditingRowBackup(null);
  }, [editingRowBackup, editingRowId]);

  const gridColumnDefs = useMemo(() => {
    const baseDefs = columnMeta.map((column) => ({
      field: column.field,
      headerName: column.header,
      editable: (params) => params?.data?.id === editingRowId,
      filter: 'agTextColumnFilter',
      floatingFilter: !isMobile,
      sortable: true,
      resizable: true,
      minWidth: isMobile ? 120 : 150,
      flex: 1,
      valueFormatter: isLikelyDateHeader(column.header)
        ? (params) => formatDateBrDisplay(params.value)
        : undefined,
    }));

    if (hasGrupoColumn) {
      baseDefs.push({
        field: 'grupo',
        headerName: 'Grupo',
        editable: (params) => params?.data?.id === editingRowId,
        filter: 'agTextColumnFilter',
        floatingFilter: !isMobile,
        sortable: true,
        resizable: true,
        minWidth: isMobile ? 120 : 140,
      });
    }

    baseDefs.push({
      field: '__actions__',
      headerName: 'Acoes',
      editable: false,
      filter: false,
      sortable: false,
      resizable: false,
      pinned: 'right',
      minWidth: 100,
      maxWidth: 140,
      cellRenderer: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
          {editingRowId === params.data.id ? (
            <>
              <IconButton
                color="success"
                size="small"
                onClick={handleSaveRowEdit}
                aria-label="Salvar linha"
              >
                <Check fontSize="small" />
              </IconButton>
              <IconButton
                color="inherit"
                size="small"
                onClick={handleCancelRowEdit}
                aria-label="Cancelar edicao"
              >
                <Close fontSize="small" />
              </IconButton>
            </>
          ) : (
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleStartRowEdit(params.data)}
              aria-label="Editar linha"
              disabled={Boolean(editingRowId)}
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
          <IconButton
            color="error"
            size="small"
            onClick={() => handleDeleteRow(params.data.id)}
            aria-label="Excluir linha"
            disabled={Boolean(editingRowId && editingRowId !== params.data.id)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      ),
    });

    return baseDefs;
  }, [
    columnMeta,
    editingRowId,
    handleCancelRowEdit,
    handleDeleteRow,
    handleSaveRowEdit,
    handleStartRowEdit,
    hasGrupoColumn,
    isMobile,
  ]);

  const handleGridCellValueChanged = (event) => {
    if (event?.data?.id !== editingRowId) return;
    handleCellChange(event.data.id, event.colDef.field, event.newValue ?? '');
  };

  const agLocaleText = {
    contains: 'Contém',
    notContains: 'Não contém',
    equals: 'Igual',
    notEqual: 'Diferente',
    startsWith: 'Começa com',
    endsWith: 'Termina com',
    blank: 'Em branco',
    notBlank: 'Não em branco',
    andCondition: 'E',
    orCondition: 'OU',
    filterOoo: 'Filtrar...',
    applyFilter: 'Aplicar',
    resetFilter: 'Limpar',
    clearFilter: 'Limpar',
    cancelFilter: 'Cancelar',
    textFilter: 'Filtro de texto',
    numberFilter: 'Filtro numérico',
    dateFilter: 'Filtro de data',
    setFilter: 'Filtro de seleção',
    columns: 'Colunas',
    filters: 'Filtros',
    sortAscending: 'Ordem crescente',
    sortDescending: 'Ordem decrescente',
    noSort: 'Sem ordenação',
    pinColumn: 'Fixar coluna',
    pinLeft: 'Fixar à esquerda',
    pinRight: 'Fixar à direita',
    noPin: 'Sem fixação',
    autosizeThisColumn: 'Autoajustar esta coluna',
    autosizeAllColumns: 'Autoajustar todas as colunas',
    resetColumns: 'Redefinir colunas',
    copy: 'Copiar',
    copyWithHeaders: 'Copiar com cabeçalhos',
    paste: 'Colar',
    export: 'Exportar',
    csvExport: 'Exportar CSV',
    excelExport: 'Exportar Excel',
    firstPage: 'Primeira página',
    previousPage: 'Página anterior',
    nextPage: 'Próxima página',
    lastPage: 'Última página',
    of: 'de',
    to: 'até',
    page: 'Página',
    more: 'mais',
    loadingOoo: 'Carregando...',
    noRowsToShow: 'Nenhum registro encontrado',
    enabled: 'Ativado',
  };

  const gridMinWidth = useMemo(() => {
    const totalCols = columnMeta.length + (hasGrupoColumn ? 1 : 0) + 1;
    return isMobile ? Math.max(720, totalCols * 120) : Math.max(980, totalCols * 160);
  }, [columnMeta.length, hasGrupoColumn, isMobile]);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');

    try {
      const parsed = await parseWorkbook(file);
      if (!parsed.rows.length) {
        throw new Error('Nao foram encontrados dados validos abaixo do cabecalho.');
      }

      if (token) {
        await uploadSharedDatasetFile(file);
      }

      setDataset(parsed);
      window.localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(parsed));
      setSearchText('');
      setGroupFilter('TODOS');
      setUnitFilter('TODAS');
      setStatusFilter('TODOS');
      setOrgaoFilter('TODOS');
    } catch (uploadError) {
      setError(uploadError.message || 'Falha ao importar a planilha.');
    } finally {
      event.target.value = '';
    }
  };

  const handleResetFilters = () => {
    setSearchText('');
    setGroupFilter('TODOS');
    setUnitFilter('TODAS');
    setStatusFilter('TODOS');
    setOrgaoFilter('TODOS');
    setQuickViewMode('cadastro');
  };

  const handleClear = () => {
    setDataset({ headers: [], fields: [], rows: [], fileName: '' });
    setError('');
    handleResetFilters();
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/financeiro')}>
          Voltar
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Processos
        </Typography>
      </Stack>

      <Paper sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Importar planilha modelo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Envie um arquivo Excel (.xlsx/.xls). Os campos e visualizacoes sao montados
              automaticamente com base nos cabecalhos da planilha.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Modelo aplicado: acompanhamento de processos (Prefeitura/CETESB), com foco em
              processo, status, orgao, prazos e responsaveis.
            </Typography>
            {dataset.fileName && (
              <Chip
                sx={{ mt: 1.5 }}
                icon={<Description />}
                label={`Arquivo atual: ${dataset.fileName}`}
              />
            )}
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
            <Button component="label" variant="contained" startIcon={<CloudUpload />}>
              Importar Excel
              <input hidden type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleClear}
              disabled={!dataset.rows.length}
            >
              Limpar
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {!dataset.rows.length ? (
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Dashboard sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Dashboard aguardando planilha
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Assim que voce importar a planilha de Processos, os indicadores, graficos e tabela serao
            criados automaticamente.
          </Typography>
          {loadingShared && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Carregando planilha compartilhada...
            </Typography>
          )}
        </Paper>
      ) : (
        <Stack spacing={2.5}>
          <Paper sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              Filtros
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pesquisar"
                  size="small"
                  placeholder="Digite qualquer termo"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" disabled={!hasGrupoColumn}>
                  <InputLabel id="group-filter-label">Grupo</InputLabel>
                  <Select
                    labelId="group-filter-label"
                    label="Grupo"
                    value={groupFilter}
                    onChange={(event) => setGroupFilter(event.target.value)}
                  >
                    <MenuItem value="TODOS">Todos</MenuItem>
                    {availableGroups.map((group) => (
                      <MenuItem key={group} value={group}>
                        {group}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" disabled={!fieldByName.unidade}>
                  <InputLabel id="unit-filter-label">Unidade</InputLabel>
                  <Select
                    labelId="unit-filter-label"
                    label="Unidade"
                    value={unitFilter}
                    onChange={(event) => setUnitFilter(event.target.value)}
                  >
                    <MenuItem value="TODAS">Todas</MenuItem>
                    {availableUnits.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" disabled={!fieldByName.status}>
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    label="Status"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                  >
                    <MenuItem value="TODOS">Todos</MenuItem>
                    {availableStatuses.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" disabled={!fieldByName.orgao}>
                  <InputLabel id="orgao-filter-label">Orgao</InputLabel>
                  <Select
                    labelId="orgao-filter-label"
                    label="Orgao"
                    value={orgaoFilter}
                    onChange={(event) => setOrgaoFilter(event.target.value)}
                  >
                    <MenuItem value="TODOS">Todos</MenuItem>
                    {availableOrgaos.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button fullWidth variant="outlined" color="inherit" onClick={handleResetFilters}>
                  Limpar filtros
                </Button>
              </Grid>
            </Grid>
          </Paper>
          <Paper
            sx={{
              p: 2,
              borderRadius: 3,
              background:
                'linear-gradient(135deg, rgba(3,105,161,0.10) 0%, rgba(8,145,178,0.08) 45%, rgba(2,132,199,0.12) 100%)',
              border: '1px solid rgba(2,132,199,0.18)',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Painel de Acompanhamento de Processos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estrutura alinhada ao modelo THORS: status, prazos, orgaos, timeline e alertas.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label="Cadastro e controle"
                  color="primary"
                  variant={quickViewMode === 'cadastro' ? 'filled' : 'outlined'}
                  clickable
                  onClick={() => setQuickViewMode('cadastro')}
                />
                <Chip
                  size="small"
                  label="Comunique-se"
                  color="warning"
                  variant={quickViewMode === 'comunique' ? 'filled' : 'outlined'}
                  clickable
                  onClick={() => setQuickViewMode('comunique')}
                />
                <Chip
                  size="small"
                  label="Alertas de prazo"
                  color="error"
                  variant={quickViewMode === 'alertas' ? 'filled' : 'outlined'}
                  clickable
                  onClick={() => setQuickViewMode('alertas')}
                />
              </Stack>
            </Stack>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Processos ativos
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {painelGestao.ativos}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Aprovacoes
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {painelGestao.aprovados}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Comunique-ses pendentes
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {painelGestao.comuniquePendentes}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Processos atrasados
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {dashboard.totalAtrasados}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.2 }}>
                  Distribuicao por status
                </Typography>
                <Stack spacing={1}>
                  {painelGestao.statusCounts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Sem status disponivel na planilha.
                    </Typography>
                  ) : (
                    painelGestao.statusCounts.slice(0, 8).map((item) => (
                      <Box key={item.label}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                          <Typography variant="body2">{item.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.value} ({item.percent}%)
                          </Typography>
                        </Stack>
                        <Box sx={{ height: 8, bgcolor: '#e2e8f0', borderRadius: 999 }}>
                          <Box
                            sx={{
                              width: `${item.percent}%`,
                              height: '100%',
                              bgcolor: '#0ea5e9',
                              borderRadius: 999,
                            }}
                          />
                        </Box>
                      </Box>
                    ))
                  )}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.2 }}>
                  Orgaos publicos monitorados
                </Typography>
                <Stack spacing={1}>
                  {painelGestao.orgaoCounts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Sem informacao de orgao na planilha.
                    </Typography>
                  ) : (
                    painelGestao.orgaoCounts.map((item) => (
                      <Stack key={item.label} direction="row" justifyContent="space-between">
                        <Typography variant="body2">{item.label}</Typography>
                        <Chip size="small" label={`${item.value} processo(s)`} />
                      </Stack>
                    ))
                  )}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.2 }}>
                  Alertas de prazo (ate 15 dias)
                </Typography>
                <Stack spacing={0.8}>
                  {alertasPrazo.proximos.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nenhum vencimento proximo identificado.
                    </Typography>
                  ) : (
                    alertasPrazo.proximos.map((item) => (
                      <Paper
                        key={`proximo-${item.id}`}
                        variant="outlined"
                        sx={{ p: 1.2, borderRadius: 2, borderColor: '#f59e0b' }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Processo {item.processo || item.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Prazo: {formatDateBrDisplay(item.prazo)}{' '}
                          {item.status ? `• ${item.status}` : ''}
                        </Typography>
                      </Paper>
                    ))
                  )}

                  {alertasPrazo.atrasados.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1, color: '#dc2626' }}>
                        Atrasados
                      </Typography>
                      {alertasPrazo.atrasados.map((item) => (
                        <Paper
                          key={`atrasado-${item.id}`}
                          variant="outlined"
                          sx={{
                            p: 1.2,
                            borderRadius: 2,
                            borderColor: '#ef4444',
                            bgcolor: '#fff5f5',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Processo {item.processo || item.id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Prazo: {formatDateBrDisplay(item.prazo)}{' '}
                            {item.status ? `• ${item.status}` : ''}
                          </Typography>
                        </Paper>
                      ))}
                    </>
                  )}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.2 }}>
                  Timeline recente
                </Typography>
                <Stack spacing={1}>
                  {timelineProcessos.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Sem eventos datados para montar timeline.
                    </Typography>
                  ) : (
                    timelineProcessos.map((item) => (
                      <Stack
                        key={`timeline-${item.id}-${item.date.getTime()}`}
                        direction="row"
                        spacing={1.2}
                        alignItems="flex-start"
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: '#0ea5e9',
                            mt: 0.8,
                            flexShrink: 0,
                          }}
                        />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Processo {item.processo || item.id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateBrDisplay(item.date)}
                            {item.status ? ` • ${item.status}` : ''}
                            {item.orgao ? ` • ${item.orgao}` : ''}
                          </Typography>
                        </Box>
                      </Stack>
                    ))
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Base operacional de processos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cadastro, historico e controle dos processos importados da planilha.
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                <Button variant="contained" size="small" startIcon={<Add />} onClick={handleAddRow}>
                  Adicionar linha
                </Button>
              </Box>
            </Box>
            {isMobile ? (
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 2 }}
                >
                  Clique no lápis para editar uma linha.
                </Typography>
                {filteredRows.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2, textAlign: 'center' }}
                  >
                    Nenhum registro encontrado
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {filteredRows.map((row) => (
                      <Card key={row.id} sx={{ borderRadius: 2, borderLeft: '4px solid #2196f3' }}>
                        <CardContent sx={{ pb: 1 }}>
                          <Stack spacing={1.5}>
                            {Object.entries(row)
                              .filter(([key]) => !['id', 'grupo', '__actions__'].includes(key))
                              .map(([key, value]) => {
                                if (!value) return null;
                                const label =
                                  columnMeta.find((c) => c.field === key)?.header || key;
                                const displayValue = isLikelyDateHeader(label)
                                  ? formatDateBrDisplay(value)
                                  : value;
                                return (
                                  <Box key={key}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      {label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                      {displayValue}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            {row.grupo && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontWeight: 700 }}
                                >
                                  Grupo
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  {row.grupo}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 0.5,
                            p: 1.5,
                            borderTop: '1px solid #eee',
                          }}
                        >
                          {editingRowId === row.id ? (
                            <>
                              <IconButton
                                color="success"
                                size="small"
                                onClick={handleSaveRowEdit}
                                aria-label="Salvar"
                              >
                                <Check fontSize="small" />
                              </IconButton>
                              <IconButton
                                color="inherit"
                                size="small"
                                onClick={handleCancelRowEdit}
                                aria-label="Cancelar"
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleStartRowEdit(row)}
                              aria-label="Editar"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteRow(row.id)}
                            aria-label="Excluir"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: { xs: 380, md: 460 } }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}
                >
                  Clique no icone de lapis na coluna Acoes para editar uma linha.
                </Typography>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Box
                    className="ag-theme-quartz"
                    sx={{ minWidth: gridMinWidth, height: { xs: 420, md: 540 } }}
                  >
                    <AgGridReact
                      rowData={sortedRows}
                      columnDefs={gridColumnDefs}
                      defaultColDef={{
                        filter: true,
                        floatingFilter: !isMobile,
                        sortable: true,
                        resizable: true,
                      }}
                      localeText={agLocaleText}
                      onCellValueChanged={handleGridCellValueChanged}
                      suppressRowClickSelection={true}
                      pagination={true}
                      paginationPageSize={isMobile ? 10 : 25}
                      animateRows={true}
                    />
                  </Box>
                </Box>
                {!filteredRows.length && (
                  <Typography variant="body2" sx={{ py: 2 }}>
                    Nenhum registro encontrado para os filtros selecionados.
                  </Typography>
                )}
              </TableContainer>
            )}
          </Paper>
        </Stack>
      )}
    </Box>
  );
}
